import PropTypes from "prop-types";
import { Link, useLocation } from "react-router-dom";
import { DropdownWrapper, Iconify } from "@repo/design-system/components/ui";

import "./HeaderMenu.scss";

// Horizontal header menu, used instead of the sidebar when MainLayout gets headerMenuConfig.
// Takes the same config shape as the sidebar: [{ label, items: [{ menuKey, menuTitle, menuIcon, navigationPath, children }] }]
const HeaderMenu = ({ menuConfig }) => {
  const location = useLocation();
  const items = menuConfig.flatMap((section) => section.items || []);

  // Same rule as the sidebar: /scholarship must not match /scholarship-applications
  const isActiveRoute = (path) =>
    Boolean(path) && (location.pathname === path || location.pathname.startsWith(path + "/"));

  const isItemActive = (item) =>
    isActiveRoute(item.navigationPath) || Boolean(item.children?.some((child) => isActiveRoute(child.navigationPath)));

  const renderChildLinks = (children) => (
    <div className="header-menu-dropdown">
      {children.map((child) => (
        <Link key={child.menuKey || child.navigationPath} to={child.navigationPath}
          className={`header-menu-dropdown-link ${isActiveRoute(child.navigationPath) ? "active" : ""}`}>
          {child.subMenuName || child.menuTitle}
        </Link>
      ))}
    </div>
  );

  if (!items.length) return null;

  return (
    <nav className="header-menu" aria-label="Main menu">
      {/* Desktop: one link per item, parents open a dropdown */}
      <ul className="header-menu-list">
        {items.map((item) => (
          <li key={item.menuKey}>
            {item.children ? (
              <DropdownWrapper toggleElement={
                <span className={`header-menu-link ${isItemActive(item) ? "active" : ""}`}>
                  {item.menuIcon && <Iconify icon={item.menuIcon} width={18} />}
                  <span>{item.menuTitle}</span>
                  <Iconify icon="lucide:chevron-down" width={14} />
                </span>
              }>
                {renderChildLinks(item.children)}
              </DropdownWrapper>
            ) : (
              <Link to={item.navigationPath}
                className={`header-menu-link ${isItemActive(item) ? "active" : ""}`}>
                {item.menuIcon && <Iconify icon={item.menuIcon} width={18} />}
                <span>{item.menuTitle}</span>
              </Link>
            )}
          </li>
        ))}
      </ul>

      {/* Mobile: everything collapses into one menu button */}
      <div className="header-menu-mobile">
        <DropdownWrapper toggleElement={<Iconify icon="lucide:menu" width={24} />}>
          {renderChildLinks(items.flatMap((item) => item.children || [item]))}
        </DropdownWrapper>
      </div>
    </nav>
  );
};

HeaderMenu.propTypes = {
  menuConfig: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          menuKey: PropTypes.string.isRequired,
          menuTitle: PropTypes.string.isRequired,
          menuIcon: PropTypes.string,
          navigationPath: PropTypes.string,
          children: PropTypes.array,
        })
      ),
    })
  ).isRequired,
};

export default HeaderMenu;
